import * as React from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "$convex/convexApi";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { useAuthIdentity } from "@/contexts/UserProfileContext";
import { BADGE_TONES } from "@/lib/badge-tones";
import { formatDate } from "@/lib/format";
import {
  getEnterpriseBadgeClass,
  getEnterpriseBadgeLabel,
} from "@/lib/customer-tag-colors";

const DISCOUNT_TYPES = [
  { value: "percentage", label: "Percentage" },
  { value: "fixed_amount", label: "Fixed Amount" },
  { value: "free_shipping", label: "Free Shipping" },
] as const;

type DiscountType = (typeof DISCOUNT_TYPES)[number]["value"];

function valueLabel(discount: any) {
  if (discount.discountType === "percentage") return `${discount.value}%`;
  if (discount.discountType === "fixed_amount") return `$${Number(discount.value ?? 0).toFixed(2)}`;
  if (discount.discountType === "free_shipping") return `Free shipping up to $${Number(discount.value ?? 0).toFixed(2)}`;
  return String(discount.value ?? "-");
}

function statusLabel(discount: any) {
  const now = Date.now();
  if (!discount.isActive) return { label: "Inactive", className: BADGE_TONES.zinc };
  if (discount.startsAt && discount.startsAt > now) {
    return { label: "Scheduled", className: BADGE_TONES.blue };
  }
  if (discount.endsAt && discount.endsAt < now) {
    return { label: "Expired", className: BADGE_TONES.red };
  }
  return { label: "Active", className: BADGE_TONES.emerald };
}

function usageLabel(discount: any) {
  const used = Number(discount.usageCount ?? 0);
  if (typeof discount.usageLimitTotal === "number") {
    return `${used} / ${discount.usageLimitTotal}`;
  }
  return String(used);
}

function perCustomerLabel(discount: any) {
  if (typeof discount.usageLimitPerCustomer === "number") {
    return `Per customer: ${discount.usageLimitPerCustomer}`;
  }
  return "Per customer: No limit";
}

function validityLabel(discount: any) {
  return {
    startsAt: discount.startsAt ? formatDate(discount.startsAt) : "Starts immediately",
    endsAt: discount.endsAt ? formatDate(discount.endsAt) : "No end date",
  };
}

function toDateTimeLocalInput(timestamp?: number) {
  if (typeof timestamp !== "number") return "";
  const date = new Date(timestamp);
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return localDate.toISOString().slice(0, 16);
}

function fromDateTimeLocalInput(value: string) {
  if (!value.trim()) return undefined;
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? undefined : timestamp;
}

function parseOptionalInteger(value: string) {
  if (!value.trim()) return undefined;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
}

function isValidOptionalInteger(value: string) {
  return !value.trim() || parseOptionalInteger(value) !== undefined;
}

export default function AdminDiscountsPage() {
  const { userRole } = useAuthIdentity();
  const canManageDiscounts = userRole === "admin";

  const [search, setSearch] = React.useState("");
  const [showActiveOnly, setShowActiveOnly] = React.useState(false);
  const [createOpen, setCreateOpen] = React.useState(false);
  const [editTarget, setEditTarget] = React.useState<any | null>(null);
  const [analyticsTarget, setAnalyticsTarget] = React.useState<any | null>(null);

  const [createForm, setCreateForm] = React.useState({
    code: "",
    title: "",
    description: "",
    discountType: "percentage" as DiscountType,
    value: 10,
    usageLimitTotal: "",
    usageLimitPerCustomer: "",
    startsAt: "",
    endsAt: "",
    isActive: true,
  });
  const [savingCreate, setSavingCreate] = React.useState(false);

  const [editForm, setEditForm] = React.useState({
    title: "",
    description: "",
    usageLimitTotal: "",
    usageLimitPerCustomer: "",
    startsAt: "",
    endsAt: "",
    isActive: true,
  });
  const [savingEdit, setSavingEdit] = React.useState(false);

  const discounts = useQuery(
    api.discounts.getAllDiscounts,
    canManageDiscounts
      ? showActiveOnly
        ? { isActive: true, limit: 200 }
        : { limit: 200 }
      : "skip",
  );

  const discountAnalytics = useQuery(
    api.discounts.getDiscountAnalytics,
    canManageDiscounts && analyticsTarget
      ? { discountId: analyticsTarget._id }
      : "skip",
  );

  const createDiscount = useMutation(api.discounts.createDiscount);
  const updateDiscount = useMutation(api.discounts.updateDiscount);

  const filtered = React.useMemo(() => {
    if (!discounts) return [];
    if (!search.trim()) return discounts;
    const q = search.toLowerCase();
    return discounts.filter((d: any) => {
      return (
        String(d.code ?? "").toLowerCase().includes(q) ||
        String(d.title ?? "").toLowerCase().includes(q) ||
        String(d.description ?? "").toLowerCase().includes(q)
      );
    });
  }, [discounts, search]);

  const stats = React.useMemo(() => {
    const all = discounts ?? [];
    const active = all.filter((d: any) => d.isActive).length;
    const totalUses = all.reduce((sum: number, d: any) => sum + Number(d.usageCount ?? 0), 0);
    const expired = all.filter((d: any) => d.endsAt && d.endsAt < Date.now()).length;
    return { total: all.length, active, expired, totalUses };
  }, [discounts]);

  const onCreate = React.useCallback(async () => {
    if (!canManageDiscounts) {
      toast.error("Admin access required");
      return;
    }

    if (!createForm.code.trim() || !createForm.title.trim()) {
      toast.error("Code and title are required");
      return;
    }

    const startsAt = fromDateTimeLocalInput(createForm.startsAt);
    const endsAt = fromDateTimeLocalInput(createForm.endsAt);
    if (startsAt !== undefined && endsAt !== undefined && endsAt < startsAt) {
      toast.error("End date must be after start date");
      return;
    }

    if (
      !isValidOptionalInteger(createForm.usageLimitTotal) ||
      !isValidOptionalInteger(createForm.usageLimitPerCustomer)
    ) {
      toast.error("Usage limits must be whole numbers greater than 0");
      return;
    }

    const usageLimitTotal = parseOptionalInteger(createForm.usageLimitTotal);
    const usageLimitPerCustomer = parseOptionalInteger(createForm.usageLimitPerCustomer);

    setSavingCreate(true);
    try {
      const result = await createDiscount({
        code: createForm.code.trim(),
        title: createForm.title.trim(),
        description: createForm.description.trim() || undefined,
        discountType: createForm.discountType,
        value: Number(createForm.value) || 0,
        usageLimitTotal,
        usageLimitPerCustomer,
        appliesTo: "entire_order",
        customerEligibility: { type: "everyone" },
        startsAt,
        endsAt,
        isActive: createForm.isActive,
      });

      if (!result?.success) {
        toast.error(result?.message || "Failed to create discount");
        return;
      }

      toast.success("Discount created");
      setCreateOpen(false);
      setCreateForm({
        code: "",
        title: "",
        description: "",
        discountType: "percentage",
        value: 10,
        usageLimitTotal: "",
        usageLimitPerCustomer: "",
        startsAt: "",
        endsAt: "",
        isActive: true,
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to create discount");
    } finally {
      setSavingCreate(false);
    }
  }, [canManageDiscounts, createDiscount, createForm]);

  const onOpenEdit = React.useCallback((discount: any) => {
    setEditTarget(discount);
    setEditForm({
      title: discount.title ?? "",
      description: discount.description ?? "",
      usageLimitTotal:
        typeof discount.usageLimitTotal === "number" ? String(discount.usageLimitTotal) : "",
      usageLimitPerCustomer:
        typeof discount.usageLimitPerCustomer === "number"
          ? String(discount.usageLimitPerCustomer)
          : "",
      startsAt: toDateTimeLocalInput(discount.startsAt),
      endsAt: toDateTimeLocalInput(discount.endsAt),
      isActive: Boolean(discount.isActive),
    });
  }, []);

  const onSaveEdit = React.useCallback(async () => {
    if (!canManageDiscounts) {
      toast.error("Admin access required");
      return;
    }

    if (!editTarget) return;

    const startsAt = fromDateTimeLocalInput(editForm.startsAt);
    const endsAt = fromDateTimeLocalInput(editForm.endsAt);
    if (startsAt !== undefined && endsAt !== undefined && endsAt < startsAt) {
      toast.error("End date must be after start date");
      return;
    }

    if (
      !isValidOptionalInteger(editForm.usageLimitTotal) ||
      !isValidOptionalInteger(editForm.usageLimitPerCustomer)
    ) {
      toast.error("Usage limits must be whole numbers greater than 0");
      return;
    }

    const usageLimitTotal = parseOptionalInteger(editForm.usageLimitTotal);
    const usageLimitPerCustomer = parseOptionalInteger(editForm.usageLimitPerCustomer);

    setSavingEdit(true);
    try {
      const result = await updateDiscount({
        discountId: editTarget._id,
        title: editForm.title,
        description: editForm.description || undefined,
        usageLimitTotal,
        usageLimitPerCustomer,
        startsAt,
        endsAt,
        isActive: editForm.isActive,
      });
      if (!result?.success) {
        toast.error(result?.message || "Failed to update discount");
        return;
      }
      toast.success("Discount updated");
      setEditTarget(null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to update discount");
    } finally {
      setSavingEdit(false);
    }
  }, [
    canManageDiscounts,
    editForm.description,
    editForm.endsAt,
    editForm.isActive,
    editForm.startsAt,
    editForm.title,
    editForm.usageLimitPerCustomer,
    editForm.usageLimitTotal,
    editTarget,
    updateDiscount,
  ]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Discount Management</h1>
          <p className="mt-1 text-muted-foreground">Create, update, and inspect discount performance.</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} disabled={!canManageDiscounts}>
          Create Discount
        </Button>
      </div>

      {!canManageDiscounts ? (
        <Card className="border-amber-300 bg-amber-50/60 dark:border-amber-800/60 dark:bg-amber-950/25">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Admin required</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Discount management requires admin access.
            </p>
          </CardContent>
        </Card>
      ) : null}

      <section className="grid gap-4 md:grid-cols-4">
        <Stat title="Total" value={stats.total} />
        <Stat title="Active" value={stats.active} />
        <Stat title="Expired" value={stats.expired} />
        <Stat title="Total Uses" value={stats.totalUses} />
      </section>

      <Card>
        <CardHeader>
          <CardTitle>Discounts</CardTitle>
          <CardDescription>Search and manage active or historical discount codes.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search code, title, description..."
              className="max-w-md"
            />
            <div className="flex items-center gap-2">
              <Checkbox
                checked={showActiveOnly}
                onCheckedChange={(checked) => setShowActiveOnly(Boolean(checked))}
                id="activeOnly"
              />
              <Label htmlFor="activeOnly">Active only</Label>
            </div>
          </div>

          {discounts === undefined ? (
            <div className="py-10 text-center text-sm text-muted-foreground">Loading discounts...</div>
          ) : !filtered.length ? (
            <p className="py-10 text-center text-sm text-muted-foreground">No discounts found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pb-2 pr-4 font-medium">Code</th>
                    <th className="pb-2 pr-4 font-medium">Title</th>
                    <th className="pb-2 pr-4 font-medium">Type</th>
                    <th className="pb-2 pr-4 font-medium">Value</th>
                    <th className="pb-2 pr-4 font-medium">Usage</th>
                    <th className="pb-2 pr-4 font-medium">Validity</th>
                    <th className="pb-2 pr-4 font-medium">Status</th>
                    <th className="pb-2 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((discount: any) => {
                    const status = statusLabel(discount);
                    const validity = validityLabel(discount);
                    return (
                      <tr key={discount._id} className="border-b last:border-0">
                        <td className="py-3 pr-4 font-mono text-xs font-semibold">{discount.code}</td>
                        <td className="py-3 pr-4">
                          <p className="font-medium">{discount.title}</p>
                          {discount.description ? (
                            <p className="text-xs text-muted-foreground">{discount.description}</p>
                          ) : null}
                        </td>
                        <td className="py-3 pr-4">{discount.discountType}</td>
                        <td className="py-3 pr-4 font-medium">{valueLabel(discount)}</td>
                        <td className="py-3 pr-4">
                          <p className="font-medium">{usageLabel(discount)}</p>
                          <p className="text-xs text-muted-foreground">{perCustomerLabel(discount)}</p>
                        </td>
                        <td className="py-3 pr-4 text-xs text-muted-foreground">
                          <p>{validity.startsAt}</p>
                          <p>{validity.endsAt}</p>
                        </td>
                        <td className="py-3 pr-4">
                          <Badge variant="outline" className={status.className}>
                            {status.label}
                          </Badge>
                        </td>
                        <td className="py-3">
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => onOpenEdit(discount)}>
                              Edit
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setAnalyticsTarget(discount)}>
                              Analytics
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>Create Discount</DialogTitle>
            <DialogDescription>Define a discount code and apply basic targeting defaults.</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            <div className="grid gap-2 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Code</Label>
                <Input
                  value={createForm.code}
                  onChange={(e) => setCreateForm((p) => ({ ...p, code: e.target.value.toUpperCase() }))}
                  placeholder="WELCOME10"
                />
              </div>
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={createForm.title}
                  onChange={(e) => setCreateForm((p) => ({ ...p, title: e.target.value }))}
                  placeholder="Welcome discount"
                />
              </div>
            </div>

            <div className="grid gap-2 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Type</Label>
                <Select
                  value={createForm.discountType}
                  onValueChange={(v) => setCreateForm((p) => ({ ...p, discountType: v as DiscountType }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DISCOUNT_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Value</Label>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={createForm.value}
                  onChange={(e) =>
                    setCreateForm((p) => ({ ...p, value: Number.parseFloat(e.target.value || "0") || 0 }))
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={createForm.description}
                onChange={(e) => setCreateForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="Optional internal summary..."
              />
            </div>

            <div className="grid gap-2 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Total usage limit</Label>
                <Input
                  type="number"
                  min={1}
                  step="1"
                  value={createForm.usageLimitTotal}
                  onChange={(e) =>
                    setCreateForm((p) => ({ ...p, usageLimitTotal: e.target.value }))
                  }
                  placeholder="Unlimited"
                />
              </div>
              <div className="space-y-2">
                <Label>Per-customer limit</Label>
                <Input
                  type="number"
                  min={1}
                  step="1"
                  value={createForm.usageLimitPerCustomer}
                  onChange={(e) =>
                    setCreateForm((p) => ({ ...p, usageLimitPerCustomer: e.target.value }))
                  }
                  placeholder="Unlimited"
                />
              </div>
            </div>

            <div className="grid gap-2 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Starts at</Label>
                <Input
                  type="datetime-local"
                  value={createForm.startsAt}
                  onChange={(e) => setCreateForm((p) => ({ ...p, startsAt: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Ends at</Label>
                <Input
                  type="datetime-local"
                  value={createForm.endsAt}
                  onChange={(e) => setCreateForm((p) => ({ ...p, endsAt: e.target.value }))}
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                checked={createForm.isActive}
                onCheckedChange={(checked) => setCreateForm((p) => ({ ...p, isActive: Boolean(checked) }))}
                id="new-discount-active"
              />
              <Label htmlFor="new-discount-active">Active on create</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => void onCreate()} disabled={savingCreate || !canManageDiscounts}>
              {savingCreate ? "Creating..." : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(editTarget)} onOpenChange={(open) => !open && setEditTarget(null)}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Edit Discount</DialogTitle>
            <DialogDescription>{editTarget?.code}</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input
                value={editForm.title}
                onChange={(e) => setEditForm((p) => ({ ...p, title: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={editForm.description}
                onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))}
              />
            </div>
            <div className="grid gap-2 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Total usage limit</Label>
                <Input
                  type="number"
                  min={1}
                  step="1"
                  value={editForm.usageLimitTotal}
                  onChange={(e) => setEditForm((p) => ({ ...p, usageLimitTotal: e.target.value }))}
                  placeholder="Unlimited"
                />
              </div>
              <div className="space-y-2">
                <Label>Per-customer limit</Label>
                <Input
                  type="number"
                  min={1}
                  step="1"
                  value={editForm.usageLimitPerCustomer}
                  onChange={(e) =>
                    setEditForm((p) => ({ ...p, usageLimitPerCustomer: e.target.value }))
                  }
                  placeholder="Unlimited"
                />
              </div>
            </div>
            <div className="grid gap-2 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Starts at</Label>
                <Input
                  type="datetime-local"
                  value={editForm.startsAt}
                  onChange={(e) => setEditForm((p) => ({ ...p, startsAt: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Ends at</Label>
                <Input
                  type="datetime-local"
                  value={editForm.endsAt}
                  onChange={(e) => setEditForm((p) => ({ ...p, endsAt: e.target.value }))}
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={editForm.isActive}
                onCheckedChange={(checked) => setEditForm((p) => ({ ...p, isActive: Boolean(checked) }))}
                id="edit-discount-active"
              />
              <Label htmlFor="edit-discount-active">Active</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)}>
              Cancel
            </Button>
            <Button onClick={() => void onSaveEdit()} disabled={savingEdit || !canManageDiscounts}>
              {savingEdit ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(analyticsTarget)}
        onOpenChange={(open) => {
          if (!open) setAnalyticsTarget(null);
        }}
      >
        <DialogContent className="sm:max-w-[640px]">
          <DialogHeader>
            <DialogTitle>Discount Analytics</DialogTitle>
            <DialogDescription>{analyticsTarget?.code}</DialogDescription>
          </DialogHeader>

          {discountAnalytics ? (
            <div className="space-y-4 py-2">
              <div className="grid gap-3 md:grid-cols-3">
                <Stat title="Total Uses" value={discountAnalytics.stats?.totalUsages ?? 0} />
                <Stat title="Total Discount" value={`$${Number(discountAnalytics.stats?.totalDiscountAmount ?? 0).toFixed(2)}`} />
                <Stat title="Avg Discount" value={`$${Number(discountAnalytics.stats?.averageDiscountAmount ?? 0).toFixed(2)}`} />
              </div>

              <div className="space-y-2">
                <h3 className="text-sm font-semibold">Recent Usage</h3>
                {discountAnalytics.recentUsages?.length ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-muted-foreground">
                          <th className="pb-2 pr-4 font-medium">Order</th>
                          <th className="pb-2 pr-4 font-medium">Customer</th>
                          <th className="pb-2 pr-4 font-medium">Enterprise</th>
                          <th className="pb-2 pr-4 font-medium">Email</th>
                          <th className="pb-2 font-medium">Discount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {discountAnalytics.recentUsages.map((usage: any) => (
                          <tr key={usage._id} className="border-b last:border-0">
                            <td className="py-2 pr-4 font-mono text-xs">{usage.orderNumber || "-"}</td>
                            <td className="py-2 pr-4">{usage.companyName || "-"}</td>
                            <td className="py-2 pr-4">
                              <Badge
                                variant="outline"
                                className={getEnterpriseBadgeClass(usage.isEnterprise)}
                              >
                                {getEnterpriseBadgeLabel(usage.isEnterprise)}
                              </Badge>
                            </td>
                            <td className="py-2 pr-4 text-xs text-muted-foreground">{usage.email || "-"}</td>
                            <td className="py-2">${Number(usage.discountAmount ?? 0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No usage data yet.</p>
                )}
              </div>
            </div>
          ) : (
            <p className="py-8 text-center text-sm text-muted-foreground">Loading analytics...</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ title, value }: { title: string; value: number | string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs text-muted-foreground">{title}</p>
        <p className="mt-1 text-2xl font-bold">{value}</p>
      </CardContent>
    </Card>
  );
}
